<template>
    <section class="section schedule-management-page">
        <div class="container-fluid schedule-container-fluid" v-if="initDone">
            <div class="columns">
                <div class="column is-12 is-flex is-align-items-center is-justify-content-space-between is-flex-wrap-wrap">
                    <div class="is-flex is-align-items-center is-flex-wrap-wrap">
                        <h1 class="title mr-2 mb-0">
                            <span class="icon">
                                <i class="fas fa-calendar-alt"></i>
                            </span>
                            <span>{{ translate("admin_schedule_manage.title") }}</span>
                        </h1>
                        <div class="select mr-5" :class="{ 'is-loading': mainStore.hasPendingRequests }">
                            <select
                                id="schedule-picker"
                                data-testid="schedule-picker"
                                :value="currentScheduleId"
                                :disabled="mainStore.hasPendingRequests"
                                :aria-label="translate('admin_schedule_manage.title')"
                                @change="onScheduleSelected"
                            >
                                <option v-for="option in scheduleOptions" :key="option.id" :value="option.id">{{ option.name }}</option>
                            </select>
                        </div>

                        <div class="buttons are-small">
                            <a :href="schedulesListingUrl" class="button">
                                <span class="icon">
                                    <i class="fas fa-arrow-left"></i>
                                </span>
                                <span>{{ translate("common.go_back_to_list") }}</span>
                            </a>

                            <a class="button is-text" :href="helpCenter().howToPlanSchedule()">
                                <span class="icon">
                                    <i class="fas fa-info-circle"></i>
                                </span>
                                <span>{{ translate("common.read_more") }}</span>
                            </a>
                        </div>
                    </div>

                    <div class="is-flex is-align-items-center">
                        <label class="label mb-0 mr-2 is-size-7 is-hidden-mobile">{{
                            translate("admin_schedule_manage.mode_label")
                        }}</label>
                        <SegmentedToggle
                            :options="editModeOptions"
                            :model-value="showScheduleStore.editMode"
                            size="small"
                            @change="onEditModeChange"
                        />
                    </div>
                </div>
            </div>

            <MatNavigationBar :key="currentScheduleId" @conflictsThresholdChanged="onConflictsThresholdChanged" />

            <ShowSchedule
                ref="showSchedule"
                :key="currentScheduleId"
                :current-user-id="props.currentUserId"
                @schedule-gone="onScheduleGone"
            />

            <KeyboardShortcutsHint class="is-hidden-touch" />
        </div>

        <div v-else class="container">
            <div class="columns is-mobile is-centered">
                <LoadingSpinner2 class="has-added-margin" />
            </div>
        </div>

        <ConflictsDetailsModal ref="conflictsDetailsModal" :key="`conflicts-${currentScheduleId}`" />
        <SliceToolModal ref="sliceToolModal" :key="`slice-${currentScheduleId}`" @config-saved="onBracketSlicingConfigSaved" />
        <DistributeToolModal ref="distributeToolModal" :key="`distribute-${currentScheduleId}`" />
    </section>
</template>

<script setup lang="ts">
    import { ref, computed, nextTick, onMounted, onBeforeUnmount } from "vue";
    import type { ComponentExposed } from "vue-component-type-helpers";
    import { translate } from "../../Common/translations";
    import { handleError } from "../../Common/errorHandler";
    import { useFeatureAnnouncement } from "../../Common/FeatureAnnouncements/useFeatureAnnouncement";
    import type HttpError from "../../Api/Utils/HttpError";
    import schedulesApi from "../../Api/schedules";
    import type { Schedule } from "../../Common/Schedule/types";
    import urls from "../../Urls/urls";
    import type { BracketSlicingTarget } from "./dropZoneItems";
    import { useScheduleMainStore } from "./useScheduleMainStore";
    import { useShowScheduleStore } from "./useShowScheduleStore";
    import {
        getConflictsThresholdFromLocalStorage,
        getScheduleMatsMultiRowModeFromLocalStorage,
        getEditModeFromSessionStorage,
        saveEditModeToSessionStorage,
    } from "./scheduleManageApp";
    import {
        eventBus,
        ON_DISTRIBUTE_TOOL_MODAL_OPENED,
        ON_SLICE_TOOL_MODAL_OPENED,
        ON_SHOW_CONFLICTS_FOR_CATEGORY,
        ON_SHOW_CONFLICTS_MODAL,
    } from "./eventBus";
    import ShowSchedule from "./ShowSchedule.vue";
    import MatNavigationBar from "./MatNavigationBar.vue";
    import KeyboardShortcutsHint from "./KeyboardShortcutsHint.vue";
    import LoadingSpinner2 from "../../Common/LoadingSpinner2.vue";
    import ConflictsDetailsModal from "./ConflictsDetailsModal.vue";
    import SliceToolModal from "./SliceToolModal.vue";
    import DistributeToolModal from "./DistributeToolModal.vue";
    import SegmentedToggle from "../../Common/SegmentedToggle.vue";
    import type { SegmentedToggleOption } from "../../Common/SegmentedToggle.vue";

    interface Props {
        eventId: number;
        eventTimezone: string;
        scheduleId: number;
        currentUserId: number;
        scheduleUrl: string;
        schedulesListingUrl: string;
    }

    const props = defineProps<Props>();

    // Pinia stores
    const mainStore = useScheduleMainStore();
    const showScheduleStore = useShowScheduleStore();

    // Type definitions for Vue 2 child components (not yet refactored)
    interface ConflictsDetailsModalComponent {
        openAll(): void;
        openCategory(scheduledCategoryId: number): void;
    }

    // Refs to child components
    const showSchedule = ref<ComponentExposed<typeof ShowSchedule> | null>(null);
    const conflictsDetailsModal = ref<ConflictsDetailsModalComponent | null>(null);
    const sliceToolModal = ref<ComponentExposed<typeof SliceToolModal> | null>(null);
    const distributeToolModal = ref<ComponentExposed<typeof DistributeToolModal> | null>(null);

    // State
    const initDone = ref(false);

    /** Schedule currently being managed. Starts as the one the page was opened with, changes on in-place switch. */
    const currentScheduleId = ref(props.scheduleId);
    const schedules = ref<Schedule[]>([]);

    const schedule = ref<Schedule>({
        id: 0,
        eventId: 0,
        name: "...",
        sharing: 0,
        startTime: "",
        plannedVersionAvailable: false,
    });

    const scheduleOptions = computed(() => {
        if (schedules.value.some(s => s.id === schedule.value.id)) {
            return schedules.value;
        }

        return [schedule.value, ...schedules.value];
    });

    const editModeOptions: SegmentedToggleOption[] = [
        { value: false, label: translate("admin_schedule_manage.mode_read_only") },
        { value: true, label: translate("admin_schedule_manage.mode_edit") },
    ];

    function helpCenter() {
        return urls.helpCenter();
    }

    function onEditModeChange(value: string | number | boolean) {
        const editModeEnabled = !!value;
        showScheduleStore.editMode = editModeEnabled;
        if (!editModeEnabled) {
            showScheduleStore.clearSelection();
        }
        saveEditModeToSessionStorage(props.eventId, editModeEnabled);
    }

    function fetchSchedule(): Promise<void> {
        return mainStore
            .trackRequest(schedulesApi.getRawScheduleById(props.eventId, currentScheduleId.value))
            .then((rawSchedule: Schedule) => {
                schedule.value = rawSchedule;
            });
    }

    /**
     * The list only feeds the schedule picker, so a failure must not block the page - the picker then offers just the current schedule.
     */
    function fetchSchedules(): Promise<void> {
        return mainStore
            .trackRequest(schedulesApi.getSchedules(props.eventId))
            .then(response => {
                schedules.value = response.all_schedules;
            })
            .catch(handleError);
    }

    function urlForSchedule(scheduleId: number): string {
        return window.location.pathname.replace(/\/schedules\/\d+\/?$/, `/schedules/${scheduleId}`);
    }

    function scheduleIdFromUrl(): number | null {
        const match = window.location.pathname.match(/\/schedules\/(\d+)\/?$/);

        return match ? Number(match[1]) : null;
    }

    /**
     * Swaps the managed schedule without reloading the page. Components are re-created through :key and fetch their own data on mount.
     */
    function switchSchedule(scheduleId: number): Promise<void> {
        showScheduleStore.resetForSchedule();
        mainStore.dropZoneItems = [];
        mainStore.scheduleId = scheduleId;
        currentScheduleId.value = scheduleId;

        return Promise.all([fetchSchedule(), mainStore.refreshDropZoneItems()])
            .then(() => undefined)
            .catch(onSwitchError);
    }

    function onScheduleSelected(event: Event) {
        const scheduleId = Number((event.target as HTMLSelectElement).value);
        if (scheduleId === currentScheduleId.value) {
            return;
        }

        window.history.pushState({}, "", urlForSchedule(scheduleId));
        switchSchedule(scheduleId);
    }

    function onPopState() {
        const scheduleId = scheduleIdFromUrl();
        if (scheduleId === null || scheduleId === currentScheduleId.value) {
            return;
        }

        // Browser navigation can't be blocked like the picker, so with requests in flight a reload is the safe way to follow the URL.
        if (mainStore.hasPendingRequests) {
            window.location.reload();
            return;
        }

        switchSchedule(scheduleId);
    }

    function onSwitchError(err: HttpError) {
        if (err.statusCode === 404) {
            onScheduleGone();
            return;
        }
        handleError(err);
    }

    function onConflictsThresholdChanged() {
        showSchedule.value?.fetchMatSchedules();
    }

    function onBracketSlicingConfigSaved() {
        mainStore.refreshDropZoneItems().catch(handleError);
    }

    function onScheduleGone() {
        window.location.href = props.schedulesListingUrl;
    }

    function handleArrowKeyScroll(event: KeyboardEvent) {
        // Only handle left/right arrow keys
        if (event.key !== "ArrowLeft" && event.key !== "ArrowRight") {
            return;
        }

        // Don't scroll if user is typing in an input field
        const activeElement = document.activeElement;
        const isTypingInInput =
            activeElement &&
            (activeElement.tagName === "INPUT" ||
                activeElement.tagName === "TEXTAREA" ||
                activeElement.tagName === "SELECT" ||
                (activeElement as HTMLElement).isContentEditable);

        if (isTypingInInput) {
            return;
        }

        // Find the scrollable container
        const container = document.querySelector(".mat-schedules-container");
        if (container) {
            // Prevent default behavior (page scroll)
            event.preventDefault();

            const scrollAmount = 400; // One mat width
            const direction = event.key === "ArrowLeft" ? -1 : 1;

            container.scrollBy({
                left: scrollAmount * direction,
                behavior: "smooth",
            });
        }
    }

    // Initialization (equivalent to created hook)
    mainStore.eventId = props.eventId;
    mainStore.eventTimezone = props.eventTimezone;
    mainStore.scheduleId = props.scheduleId;

    showScheduleStore.conflictsThreshold = getConflictsThresholdFromLocalStorage(props.eventId);
    showScheduleStore.multiRowMode = getScheduleMatsMultiRowModeFromLocalStorage(props.eventId);
    showScheduleStore.editMode = getEditModeFromSessionStorage(props.eventId);
    showScheduleStore.scheduledCategorySearchQuery = "";

    // Mounted hook
    onMounted(() => {
        eventBus.$on(ON_SHOW_CONFLICTS_MODAL, () => {
            conflictsDetailsModal.value?.openAll();
        });
        eventBus.$on(ON_SHOW_CONFLICTS_FOR_CATEGORY, (scheduledCategoryId: number) => {
            conflictsDetailsModal.value?.openCategory(scheduledCategoryId);
        });
        eventBus.$on(ON_SLICE_TOOL_MODAL_OPENED, (target: BracketSlicingTarget) => {
            sliceToolModal.value?.open(target);
        });
        eventBus.$on(ON_DISTRIBUTE_TOOL_MODAL_OPENED, () => {
            distributeToolModal.value?.open();
        });

        // Add arrow key scroll listener
        window.addEventListener("keydown", handleArrowKeyScroll);
        window.addEventListener("popstate", onPopState);

        return Promise.all([fetchSchedule(), fetchSchedules(), mainStore.refreshDropZoneItems()])
            .catch((err: HttpError) => {
                if (err.statusCode === 404) {
                    onScheduleGone();
                    return;
                }
                handleError(err);
            })
            .finally(() => {
                initDone.value = true;

                if (props.currentUserId) {
                    nextTick(() => {
                        useFeatureAnnouncement().showEligible(props.currentUserId, [
                            { key: "schedule_management_schedule_picker", element: "#schedule-picker" },
                        ]);
                    });
                }
            });
    });

    // Cleanup hook
    onBeforeUnmount(() => {
        // Remove arrow key scroll listener
        window.removeEventListener("keydown", handleArrowKeyScroll);
        window.removeEventListener("popstate", onPopState);
    });
</script>
